import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { useDispatch } from 'react-redux'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Alert } from 'components/ui/alert'
import { ShieldCheck, Loader2, RefreshCw } from 'lucide-react'

interface VerificationCodePopupCNProps {
	email: string
	name: string
	onSuccess: () => void
	onCancel: () => void
}

const CODE_EXPIRY_SECONDS = 600 // 10 minutes
const RESEND_COOLDOWN_SECONDS = 60

const VerificationCodePopupCN: React.FC<VerificationCodePopupCNProps> = ({
	email,
	name,
	onSuccess,
	onCancel,
}) => {
	const [verificationCode, setVerificationCode] = useState('')
	const [errorMessage, setErrorMessage] = useState('')
	const [isVerifying, setIsVerifying] = useState(false)
	const [timeRemaining, setTimeRemaining] = useState(CODE_EXPIRY_SECONDS)
	const [resendCooldown, setResendCooldown] = useState(0)
	const dispatch = useDispatch()

	// Countdown timer for code expiry
	useEffect(() => {
		if (timeRemaining <= 0) return
		const timer = setInterval(() => {
			setTimeRemaining(prev => {
				if (prev <= 1) {
					clearInterval(timer)
					return 0
				}
				return prev - 1
			})
		}, 1000)
		return () => clearInterval(timer)
	}, [timeRemaining])

	// Resend cooldown timer
	useEffect(() => {
		if (resendCooldown <= 0) return
		const timer = setInterval(() => {
			setResendCooldown(prev => {
				if (prev <= 1) {
					clearInterval(timer)
					return 0
				}
				return prev - 1
			})
		}, 1000)
		return () => clearInterval(timer)
	}, [resendCooldown])

	const formatTime = useCallback((seconds: number): string => {
		const mins = Math.floor(seconds / 60)
		const secs = seconds % 60
		return `${mins}:${secs.toString().padStart(2, '0')}`
	}, [])

	const handleVerify = async (e: React.FormEvent) => {
		e.preventDefault()
		setErrorMessage('')

		if (verificationCode.trim().length !== 6) {
			setErrorMessage('Please enter a 6-digit verification code')
			return
		}

		if (timeRemaining <= 0) {
			setErrorMessage('Verification code has expired. Please request a new one.')
			return
		}

		setIsVerifying(true)

		try {
			const firstName = name.split(' ')[0] || name

			const response = await axios.post('/api/users/verify-code', {
				email,
				code: verificationCode,
				name,
				firstName,
			})

			if (response.data.success) {
				const userData = response.data.user
				localStorage.setItem('userInfo', JSON.stringify(userData))

				dispatch({
					type: 'auth/login/fulfilled',
					payload: userData,
				} as any)

				onSuccess()
			} else {
				setErrorMessage(response.data.message || 'Invalid verification code')
			}
		} catch (error: any) {
			setErrorMessage(
				error.response?.data?.message || 'Invalid verification code. Please try again.',
			)
		} finally {
			setIsVerifying(false)
		}
	}

	const handleResendCode = async () => {
		if (resendCooldown > 0) return

		setErrorMessage('')
		try {
			const firstName = name.split(' ')[0] || name
			const response = await axios.post('/api/users/send-verification-code', {
				name,
				firstName,
				email,
			})

			if (response.data.success) {
				setResendCooldown(RESEND_COOLDOWN_SECONDS)
				setTimeRemaining(CODE_EXPIRY_SECONDS)
				setVerificationCode('')
			}
		} catch (error: any) {
			setErrorMessage('Failed to resend code. Please try again.')
		}
	}

	const isExpired = timeRemaining <= 0

	return (
		<Dialog open={true} onClose={onCancel}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
							<ShieldCheck className="w-5 h-5" />
						</div>
						<div>
							<DialogTitle>Enter Verification Code</DialogTitle>
							<DialogDescription>Verify your email to complete registration</DialogDescription>
						</div>
					</div>
				</DialogHeader>

				<form onSubmit={handleVerify} className="px-5 py-4 space-y-4">
					<p className="text-sm text-gray-600">
						We've sent a 6-digit verification code to <strong>{email}</strong>
					</p>

					{/* Countdown timer */}
					<div className="flex justify-center">
						<span
							className={`text-sm font-mono px-3 py-1 rounded-full ${
								isExpired
									? 'bg-red-50 text-red-600'
									: timeRemaining <= 60
										? 'bg-yellow-50 text-yellow-700'
										: 'bg-gray-100 text-gray-600'
							}`}
						>
							{isExpired ? 'Code expired' : `Expires in ${formatTime(timeRemaining)}`}
						</span>
					</div>

					{errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

					<div>
						<Input
							type="text"
							value={verificationCode}
							onChange={e => {
								const value = e.target.value.replace(/\D/g, '').slice(0, 6)
								setVerificationCode(value)
								setErrorMessage('')
							}}
							placeholder="000000"
							maxLength={6}
							className="text-center text-2xl tracking-[8px] font-mono h-14"
							autoFocus
							disabled={isExpired}
						/>
					</div>

					<DialogFooter className="px-0 border-t border-gray-200 pt-4 flex-col gap-2">
						<Button
							type="submit"
							disabled={verificationCode.length !== 6 || isVerifying || isExpired}
							className="w-full"
						>
							{isVerifying ? (
								<>
									<Loader2 className="w-4 h-4 animate-spin" />
									Verifying...
								</>
							) : (
								'Verify Code'
							)}
						</Button>
						<div className="flex gap-2 w-full">
							<Button
								type="button"
								variant="ghost"
								onClick={handleResendCode}
								disabled={resendCooldown > 0}
								className="flex-1"
							>
								<RefreshCw className="w-3.5 h-3.5" />
								{resendCooldown > 0 ? `Resend (${resendCooldown}s)` : 'Resend Code'}
							</Button>
							<Button type="button" variant="ghost" onClick={onCancel} className="flex-1">
								Cancel
							</Button>
						</div>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	)
}

export default VerificationCodePopupCN
